import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  format,
  addDays,
  addMonths,
  addYears,
  startOfMonth,
  isBefore,
  isAfter,
  formatDistanceStrict,
} from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";
import { CalendarIcon, AlertTriangle } from "lucide-react";
import {
  AccountSubscription,
  SuccessResponse,
  AdminCreateAccountSubscriptionRequest,
  AdminUpdateAccountSubscriptionRequest,
} from "../../types/generated";
import { apiService } from "../../services/api";
import { Alert, AlertDescription } from "../ui/alert";

interface UserSubscriptionsTabProps {
  userId: string;
  userName: string;
}

const subscriptionTypes = [
  { value: "free", label: "Free", duration: (date: Date) => addYears(date, 1) },
  {
    value: "trial",
    label: "Trial",
    duration: (date: Date) => addDays(date, 7),
  },
  {
    value: "monthly",
    label: "Monthly",
    duration: (date: Date) => addMonths(startOfMonth(date), 1),
  },
] as const;

type SubscriptionType = (typeof subscriptionTypes)[number]["value"];

export function UserSubscriptionsTab({
  userId,
  userName,
}: UserSubscriptionsTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [newSubscriptionType, setNewSubscriptionType] =
    useState<SubscriptionType>("trial");
  const [startDate, setStartDate] = useState<Date>(new Date());

  // Fetch user's accounts
  const { data: accounts } = useQuery({
    queryKey: ["user-accounts", userId],
    queryFn: () => apiService.getUserAccounts(userId),
  });

  // Set default account
  useEffect(() => {
    if (accounts?.data?.[0] && !selectedAccount) {
      setSelectedAccount(accounts.data[0].id);
    }
  }, [accounts?.data, selectedAccount]);

  // Fetch subscriptions for selected account
  const { data: subscriptions, isLoading: isLoadingSubscriptions } = useQuery({
    queryKey: ["user-subscriptions", userId, selectedAccount],
    queryFn: () =>
      selectedAccount
        ? apiService.getAccountSubscriptions(userId, selectedAccount)
        : Promise.resolve({ data: [] } as SuccessResponse<
            AccountSubscription[]
          >),
    enabled: !!selectedAccount,
  });

  // Get the latest subscription end date
  const getLatestSubscriptionEndDate = () => {
    if (!subscriptions?.data?.length) return null;

    return subscriptions.data.reduce((latest, sub) => {
      const endDate = new Date(sub.expires_at);
      return latest && isAfter(latest, endDate) ? latest : endDate;
    }, null as Date | null);
  };

  // Set the latest subscription end date as the default start date
  useEffect(() => {
    const latestEndDate = getLatestSubscriptionEndDate();
    if (latestEndDate) {
      setStartDate(addDays(latestEndDate, 0));
    }
  }, [subscriptions?.data]);

  // Calculate subscription gap warning
  const getSubscriptionGapWarning = () => {
    const latestEndDate = getLatestSubscriptionEndDate();
    if (!latestEndDate || !isAfter(startDate, latestEndDate)) return null;

    const gap = formatDistanceStrict(startDate, latestEndDate);
    return `There will be a gap of ${gap} between the latest subscription and the new one.`;
  };

  // Calculate end date based on subscription type and start date
  const calculateEndDate = (type: SubscriptionType, start: Date) => {
    const subscriptionType = subscriptionTypes.find((t) => t.value === type);
    if (!subscriptionType) return start;
    return subscriptionType.duration(start);
  };

  const createSubscriptionMutation = useMutation({
    mutationFn: (data: AdminCreateAccountSubscriptionRequest) =>
      apiService.createAccountSubscription(userId, selectedAccount!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-subscriptions", userId, selectedAccount],
      });
      toast({
        title: "Success",
        description: "Subscription created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: ({
      subscriptionId,
      data,
    }: {
      subscriptionId: string;
      data: AdminUpdateAccountSubscriptionRequest;
    }) =>
      apiService.updateAccountSubscription(
        userId,
        selectedAccount!,
        subscriptionId,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-subscriptions", userId, selectedAccount],
      });
      toast({
        title: "Success",
        description: "Subscription updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCreateSubscription = () => {
    if (!selectedAccount) return;

    // Calculate end date based on subscription type
    const endDate = calculateEndDate(newSubscriptionType, startDate);

    createSubscriptionMutation.mutate({
      type: newSubscriptionType,
      starts_at: startDate.toISOString(),
      ends_at: endDate.toISOString(),
    });
  };

  const handleExtendSubscription = (subscriptionId: string, months: number) => {
    const subscription = subscriptions?.data.find(
      (s) => s.id === subscriptionId
    );
    if (!subscription || subscription.type !== "monthly") return;

    const currentExpiry = new Date(subscription.expires_at);
    const newExpiry = addMonths(startOfMonth(currentExpiry), months);

    updateSubscriptionMutation.mutate({
      subscriptionId,
      data: {
        ends_at: newExpiry.toISOString(),
      },
    });
  };

  const gapWarning = getSubscriptionGapWarning();

  return (
    <div className="space-y-6 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
        <div className="flex-1 space-y-2">
          <label className="text-sm font-medium">Select Account</label>
          <Select
            value={selectedAccount || ""}
            onValueChange={setSelectedAccount}
          >
            <SelectTrigger>
              <SelectValue translate="no" placeholder="Select an account" />
            </SelectTrigger>
            <SelectContent>
              {accounts?.data.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedAccount && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end w-full">
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">Subscription Type</label>
              <Select
                value={newSubscriptionType}
                onValueChange={(value: SubscriptionType) => {
                  setNewSubscriptionType(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {subscriptionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    id="calendar-trigger"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  aria-describedby="calendar-description"
                >
                  <p id="calendar-description" className="sr-only">
                    Select a start date for the subscription. You cannot select
                    dates that overlap with existing subscriptions.
                  </p>
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => date && setStartDate(date)}
                    initialFocus
                    fromDate={new Date()}
                    toYear={new Date().getFullYear() + 1}
                    disabled={(date) => {
                      // Find any subscription that overlaps with this date
                      return (
                        subscriptions?.data.some((sub) => {
                          const startDate = new Date(sub.started_at);
                          const endDate = new Date(sub.expires_at);
                          return date >= startDate && date <= endDate;
                        }) ?? false
                      );
                    }}
                    className="rounded-md border w-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <div className="h-10 px-3 py-2 border rounded-md bg-muted">
                {format(
                  calculateEndDate(newSubscriptionType, startDate),
                  "PPP"
                )}
              </div>
            </div>
            <Button
              onClick={handleCreateSubscription}
              disabled={!selectedAccount}
            >
              Create Subscription
            </Button>
          </div>

          {gapWarning && (
            <Alert className="mt-2 border-yellow-500 text-yellow-800 dark:text-yellow-300">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <AlertDescription>{gapWarning}</AlertDescription>
            </Alert>
          )}

          {isLoadingSubscriptions ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <div className="w-full flex-1">
              {/* Desktop View */}
              <div className="hidden md:block border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Type</TableHead>
                      <TableHead className="whitespace-nowrap">
                        Status
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Started
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Expires
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions?.data.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {subscription.type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              subscription.status === "active"
                                ? "success"
                                : "secondary"
                            }
                            className="capitalize"
                          >
                            {subscription.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {format(new Date(subscription.started_at), "PPP")}
                        </TableCell>
                        <TableCell>
                          {format(new Date(subscription.expires_at), "PPP")}
                        </TableCell>
                        <TableCell>
                          {subscription.type === "monthly" && (
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleExtendSubscription(subscription.id, 1)
                                }
                              >
                                Extend 1 Month
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleExtendSubscription(subscription.id, 12)
                                }
                              >
                                Extend 1 Year
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {subscriptions?.data.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-8"
                        >
                          No subscriptions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile View */}
              <div className="md:hidden space-y-4">
                {subscriptions?.data.map((subscription) => (
                  <Card key={subscription.id} className="p-4">
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="capitalize">
                          {subscription.type}
                        </Badge>
                        <Badge
                          variant={
                            subscription.status === "active"
                              ? "success"
                              : "secondary"
                          }
                          className="capitalize"
                        >
                          {subscription.status}
                        </Badge>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <div className="font-medium text-muted-foreground">
                            Started
                          </div>
                          <div>
                            {format(new Date(subscription.started_at), "PPP")}
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-muted-foreground">
                            Expires
                          </div>
                          <div>
                            {format(new Date(subscription.expires_at), "PPP")}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      {subscription.type === "monthly" && (
                        <div className="flex flex-col gap-2 pt-2 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleExtendSubscription(subscription.id, 1)
                            }
                          >
                            Extend 1 Month
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleExtendSubscription(subscription.id, 12)
                            }
                          >
                            Extend 1 Year
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
                {subscriptions?.data.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    No subscriptions found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
